import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FaArrowDown } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './SectionTransition.css';

/**
 * SectionTransition — A contextual "next step" prompt between sections.
 * Guides visitors naturally from one section to the next with a smart nudge.
 *
 * Props:
 *   nextSection: id of the next section to scroll to
 *   message:     short persuasive message
 *   cta:         button label (optional)
 *   route:       navigation route for CTA (optional)
 *   variant:     'light' | 'dark' (default: 'light')
 */
const SectionTransition = ({
  nextSection,
  message = 'Keep exploring',
  cta,
  route,
  variant = 'light',
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const navigate = useNavigate();

  const scrollToNext = () => {
    if (nextSection) {
      const el = document.getElementById(nextSection);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div ref={ref} className={`section-transition section-transition--${variant}`}>
      <motion.div
        className="section-transition__inner"
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
      >
        <p className="section-transition__message">{message}</p>

        <div className="section-transition__actions">
          {cta && route && (
            <button
              className="section-transition__cta"
              onClick={() => navigate(route)}
            >
              {cta}
            </button>
          )}
          <button
            className="section-transition__scroll-btn"
            onClick={scrollToNext}
            aria-label="Scroll to next section"
          >
            <motion.span
              animate={{ y: [0, 6, 0] }}
              transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
            >
              <FaArrowDown />
            </motion.span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default SectionTransition;
